import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  Activity,
  ActivityQueryParam,
  ColumnWithTranslation,
  Entity,
  insightsHeader,
  Octopus,
  OctopusResponseError,
  TaskUpdate,
  TaskFieldOption,
  TaskSearch,
  WorkflowActionFields,
  WorkflowTask,
} from '@maximizer/core/shared/domain';
import { map, Observable, of } from 'rxjs';
import { ContextService } from './context.service';

@Injectable()
export class TaskService {
  protected readonly http = inject(HttpClient);
  protected readonly context = inject(ContextService);

  private static columnKey(index: number): string {
    return `C${index}`;
  }

  private getQuery(
    params: TaskSearch,
  ): Octopus.SearchQuery<Octopus.Task> | Octopus.LogicalQuery<Octopus.Task> {
    const searchArray = [];
    if (params.key) {
      searchArray.push({
        Key: {
          $EQ: params.key,
        },
      });
    }
    if (params.workflowKey) {
      searchArray.push({
        WorkflowKey: {
          $EQ: params.workflowKey,
        },
      });
    }
    if (params.workflowStageKey) {
      searchArray.push({
        WorkflowStageKey: {
          $EQ: params.workflowStageKey,
        },
      });
    }
    if (params.abEntryKey) {
      searchArray.push({
        AbEntryKey: {
          $EQ: params.abEntryKey,
        },
      });
    }
    if (params.leadKey) {
      searchArray.push({
        LeadKey: {
          $EQ: params.leadKey,
        },
      });
    }

    if (params.completed !== undefined) {
      searchArray.push({
        Completed: {
          $EQ: params.completed,
        },
      });
    }
    if (params.workflowInstanceKey) {
      searchArray.push({
        WorkflowInstanceKey: {
          $EQ: params.workflowInstanceKey,
        },
      });
    }

    if (searchArray.length === 1) {
      return searchArray[0];
    } else if (searchArray.length > 1) {
      return {
        $AND: searchArray,
      };
    }
    return {};
  }

  create(task: Entity | Octopus.TaskCreate): Observable<string | null> {
    const request: Octopus.TaskWriteRequest = {
      Task: {
        Data: task as Partial<Octopus.Task>,
      },
      Globalization: Octopus.DefaultGlobalization,
      Compatibility: { TaskObject: '2.0' },
    };
    return this.post<Octopus.TaskWriteResponse>(
      Octopus.Action.CREATE,
      request,
    ).pipe(
      map((result) => {
        if (this.isSuccessful(result)) {
          return result.Task.Data.Key;
        }
        return null;
      }),
    );
  }

  // A failed response reads as a missing task unless failOnError is set.
  read(
    taskKey: string,
    options: { failOnError?: boolean } = {},
  ): Observable<Entity | null> {
    const request: Octopus.TaskReadRequest = {
      Task: {
        Scope: {
          Fields: {
            AbEntry: {
              Key: 1,
              ParentKey: 1,
              FullName: 1,
              CompanyName: 1,
              DisplayValue: 1,
              LastContactDate: 1,
            },
            SecStatus: {
              CanRead: 1,
              CanUpdate: 1,
            },
            WithKey: 1,
            ActionPlanKey: 1,
            Activity: 1,
            IconType: 1,
            Alarm: {
              Value: 1,
              DisplayValue: 1,
            },
            AssignedTo: {
              Key: 1,
              DisplayValue: 1,
            },
            Completed: 1,
            CompletionDate: {
              Value: 1,
              DisplayValue: 1,
            },
            Category: {
              Value: 1,
              DisplayValue: 1,
            },
            CreationDate: {
              Value: 1,
              DisplayValue: 1,
            },
            Creator: {
              Key: 1,
              DisplayValue: 1,
            },
            DateTime: {
              Value: 1,
              DisplayValue: 1,
            },
            EffectiveDate: {
              Value: 1,
              DisplayValue: 1,
            },
            Key: 1,
            LastModifyDate: {
              Value: 1,
              DisplayValue: 1,
            },
            Lead: {
              Key: 1,
              FullName: 1,
              CompanyName: 1,
              DisplayValue: 1,
            },
            ModifiedBy: {
              Key: 1,
              DisplayValue: 1,
            },
            Priority: 1,
            Recurrence: {
              Key: 1,
              Pattern: {
                Type: 1,
                Interval: 1,
                Regenerating: 1,
                DaysOfWeek: 1,
                FirstDayOfWeek: 1,
                DayOfMonth: 1,
                Month: 1,
                Index: 1,
              },
              Range: {
                StartDate: 1,
                EndDate: 1,
                Occurrences: 1,
                RecurrenceTimeZone: 1,
              },
            },
            // Without its DisplayValue, a Result item made inactive since has no label to print.
            Result: {
              Value: 1,
              DisplayValue: 1,
            },
            SnoozeUntil: 1,
            With: {
              Opportunity: { Key: 1, Objective: 1 },
              Case: {
                Key: 1,
                CaseNumber: 1,
                Subject: 1,
                FollowUpDate: { DisplayValue: 1 },
              },
            },
            WorkflowInstanceKey: 1,
            WorkflowKey: 1,
            WorkflowStageKey: 1,
            WorkflowTaskTemplateKey: 1,
          },
        },
        Criteria: {
          SearchQuery: {
            Key: {
              $EQ: taskKey,
            },
          },
        },
      },
      Globalization: Octopus.DefaultGlobalization,
      Compatibility: { TaskObject: '2.0' },
    };

    return this.post<Octopus.EntityResponse>(Octopus.Action.READ, request).pipe(
      map((result) => {
        if (options.failOnError && !this.isSuccessful(result)) {
          throw OctopusResponseError.fromResponse(
            result ?? { Code: Octopus.ResponseStatusCode.Unsuccessful },
          );
        }
        if (this.isSuccessful(result) && result.Task?.Data[0]) {
          return result.Task.Data[0];
        }
        return null;
      }),
    );
  }

  readWorkflowTask(taskKey: string): Observable<Entity | null> {
    const request: Octopus.TaskReadRequest = {
      Task: {
        Scope: {
          Fields: {
            Key: 1,
            WorkflowKey: 1,
            AbEntry: {
              Key: 1,
              DisplayValue: 1,
            },
          },
        },
        Criteria: {
          SearchQuery: {
            Key: {
              $EQ: taskKey,
            },
          },
        },
      },

      Globalization: Octopus.DefaultGlobalization,
      Compatibility: { TaskObject: '2.0' },
    };

    return this.post<Octopus.EntityResponse>(Octopus.Action.READ, request).pipe(
      map((result) => {
        if (this.isSuccessful(result) && result.Task?.Data[0]) {
          return result.Task.Data[0];
        }
        return null;
      }),
    );
  }

  readList(
    query:
      | Octopus.SearchQuery<Octopus.Task>
      | Octopus.LogicalQuery<Octopus.Task>,
  ): Observable<Octopus.Task[]> {
    const request: Octopus.TaskReadRequest = {
      Task: {
        Scope: {
          Fields: {
            Key: 1,
            Activity: 1,
            DateTime: {
              Value: 1,
              DisplayValue: 1,
            },
          },
        },
        Criteria: {
          SearchQuery: query,
        },
      },
      Globalization: Octopus.DefaultGlobalization,
      Compatibility: { TaskObject: '2.0' },
    };

    return this.post<Octopus.TaskResponse>(Octopus.Action.READ, request).pipe(
      map((result) =>
        this.isSuccessful(result) && result.Task?.Data ? result.Task.Data : [],
      ),
    );
  }

  readWorkflowStageTasks(
    params: TaskSearch,
  ): Observable<WorkflowTask[] | null> {
    const searchQuery = this.getQuery(params);
    const request: Octopus.TaskReadRequest = {
      Task: {
        Scope: {
          Fields: {
            Key: 1,
            DateTime: {
              Value: 1,
              DisplayValue: 1,
            },
            AbEntry: {
              Key: 1,
              DisplayValue: 1,
            },
            Lead: {
              Key: 1,
              DisplayValue: 1,
            },
            AssignedTo: {
              Key: 1,
              DisplayValue: 1,
            },
            Completed: 1,
            CompletionDate: {
              Value: 1,
              DisplayValue: 1,
            },
            Activity: 1,
            Priority: 1,
            WorkflowTaskTemplateKey: 1,
            WorkflowInstanceKey: 1,
          },
        },
        Criteria: {
          SearchQuery: searchQuery,
        },
      },
      Compatibility: { TaskObject: '2.0' },
    };
    return this.post<Octopus.TaskResponse>(Octopus.Action.READ, request).pipe(
      map((result) => {
        if (this.isSuccessful(result)) {
          const mapper = new Octopus.WorkflowTaskMapper();
          return mapper.from(result.Task?.Data);
        }
        return null;
      }),
    );
  }

  readOverdueTasksByWorkflowInstanceKey(
    workflowInstanceKeys: string[],
  ): Observable<WorkflowTask[] | null> {
    const overdueFilter: Octopus.SearchQuery<Octopus.Task>[] = [
      {
        DateTime: {
          $LT: '$TODAY()',
        },
      },
      {
        Completed: {
          $EQ: false,
        },
      },
    ];

    return this.readByWorkflowInstanceKey(workflowInstanceKeys, overdueFilter);
  }

  readByWorkflowInstanceKey(
    workflowInstanceKeys: string[],
    customFilters?: Octopus.SearchQuery<Octopus.Task>[],
  ): Observable<WorkflowTask[] | null> {
    if (workflowInstanceKeys.length === 0) {
      return of(null);
    }
    const searchQuery: Octopus.LogicalQuery<Octopus.Task> = {
      $AND: [
        {
          WorkflowInstanceKey: {
            $IN: workflowInstanceKeys,
          },
        },
      ],
    };
    if (customFilters && customFilters.length > 0) {
      customFilters.forEach((filter) => {
        searchQuery.$AND?.push(filter);
      });
    }
    const request: Octopus.TaskReadRequest = {
      Task: {
        Scope: {
          Fields: {
            Key: 1,
            WorkflowTaskTemplateKey: 1,
            WorkflowInstanceKey: 1,
            DateTime: {
              Value: 1,
              DisplayValue: 1,
            },
            AbEntry: {
              Key: 1,
              DisplayValue: 1,
              Type: 1,
            },
            Lead: {
              Key: 1,
              DisplayValue: 1,
            },
            AssignedTo: {
              Key: 1,
              DisplayValue: 1,
            },
            Completed: 1,
            CompletionDate: {
              Value: 1,
              DisplayValue: 1,
            },
            Activity: 1,
          },
        },
        Criteria: {
          SearchQuery: searchQuery,
        },
      },

      Compatibility: { TaskObject: '2.0' },
    };
    return this.post<Octopus.TaskResponse>(Octopus.Action.READ, request).pipe(
      map((result) => {
        if (this.isSuccessful(result)) {
          const mapper = new Octopus.WorkflowTaskMapper();
          return mapper.from(result.Task?.Data);
        }
        return null;
      }),
    );
  }

  getWorkflowInstanceKey(taskKey: string): Observable<string | null> {
    const request: Octopus.TaskReadRequest = {
      Task: {
        Scope: {
          Fields: {
            Key: 1,
            WorkflowInstanceKey: 1,
          },
        },
        Criteria: {
          SearchQuery: {
            Key: {
              $EQ: taskKey,
            },
          },
        },
      },

      Compatibility: { TaskObject: '2.0' },
    };

    return this.post<Octopus.TaskResponse>(Octopus.Action.READ, request).pipe(
      map((result) => {
        if (this.isSuccessful(result) && result.Task?.Data.length > 0) {
          return result.Task?.Data[0].WorkflowInstanceKey ?? null;
        }
        return null;
      }),
    );
  }

  update(tasks?: Entity[] | TaskUpdate[]): Observable<boolean> {
    const request = (tasks ?? []).reduce<Record<string, unknown>>(
      (result, task, index) => {
        result[`Task.${index}`] = { Data: task };
        return result;
      },
      {
        Globalization: Octopus.DefaultGlobalization,
        Compatibility: { TaskObject: '2.0' },
      },
    );

    return this.post<Octopus.ActivityResponse>(
      Octopus.Action.UPDATE,
      request,
    ).pipe(map((result) => this.isSuccessful(result)));
  }

  updateWithWorkflowResult(
    task: TaskUpdate,
    outcome: string,
  ): Observable<boolean> {
    const request: { [name: string]: unknown } = {};

    request['Task'] = {
      Data: task,
      Options: { [WorkflowActionFields.WorkflowResult]: outcome },
    };

    request['Compatibility'] = { TaskObject: '2.0' };
    request['Globalization'] = Octopus.DefaultGlobalization;

    return this.post<Octopus.ActivityResponse>(
      Octopus.Action.UPDATE,
      request,
    ).pipe(map((result) => this.isSuccessful(result)));
  }

  delete(taskKeys: string[]): Observable<boolean> {
    const request: { [name: string]: unknown } = {};

    if (taskKeys) {
      taskKeys.forEach(
        (key, index) => (request['Task.' + index] = { Data: { Key: key } }),
      );
    }

    request['Compatibility'] = { TaskObject: '2.0' };

    return this.post<Octopus.ActivityResponse>(
      Octopus.Action.DELETE,
      request,
    ).pipe(map((result) => this.isSuccessful(result)));
  }

  getFieldOption(showDisabledUsers?: boolean): Observable<TaskFieldOption> {
    const request: Octopus.TaskFieldOptionsRequest = {
      Task: {
        FieldOptions: {
          Priority: [
            {
              Key: 1,
              DisplayValue: 1,
            },
          ],
          Category: [
            {
              Key: 1,
              DisplayValue: 1,
            },
          ],
          Result: [
            {
              Key: 1,
              DisplayValue: 1,
            },
          ],
          AssignedTo: [
            {
              Key: 1,
              DisplayValue: 1,
            },
          ],
        },
        Options: {
          IncludeDisabled: showDisabledUsers,
        },
      },
    };

    return this.post<Octopus.TaskFieldOptionsResponse>(
      Octopus.Action.READ,
      request,
    ).pipe(
      map((result) => {
        if (this.isSuccessful(result)) {
          const mapper = new Octopus.TaskFieldOptionsMapper();
          return mapper.from(result);
        }
        return { priority: [], category: [], result: [], assignedTo: [] };
      }),
    );
  }

  validate(taskKey: string): Observable<{
    required: string[];
  }> {
    const request: Octopus.EntityWriteRequest = {
      Task: {
        Data: {
          Key: taskKey,
          Completed: true,
        },
      },
      Compatibility: { TaskObject: '2.0' },
    };

    return this.post<Octopus.EntityValidationResponse>(
      Octopus.Action.VALIDATE,
      request,
      false,
    ).pipe(
      map((response) => {
        if (this.isSuccessful(response)) {
          const required: string[] = [];
          const validation = response.Task?.Validation;
          if (validation) {
            Object.keys(validation).forEach((key) => {
              const rules = validation[key];
              if (rules instanceof Object) {
                Object.keys(rules).forEach((childKey) => {
                  const rule = (
                    rules as { [key: string]: { Mandatory?: boolean } }
                  )[childKey];
                  if (rule?.Mandatory) {
                    required.push(key + '/' + childKey);
                  }
                });
              }
            });
          }
          return { required };
        }
        return { required: [] };
      }),
    );
  }

  readActivities(
    param: ActivityQueryParam,
    showDisabledUsers?: boolean,
    columns?: ColumnWithTranslation[],
  ): Observable<Activity[]> {
    const query: Octopus.LogicalQuery<Octopus.Task> =
      this.getActivitiesQuery(param);
    const request = this.getRequest(query, showDisabledUsers, columns);

    return this.post<Octopus.TaskResponse>(
      Octopus.Action.READ,
      request,
      false,
    ).pipe(map((result) => this.mapResult(result, columns)));
  }

  private mapResult(
    result: Octopus.TaskResponse,
    columns?: ColumnWithTranslation[],
  ): Activity[] {
    const mapper = new Octopus.ActivityManagerTaskMapper(
      columns,
      columns?.map((_, index) => TaskService.columnKey(index)),
    );
    const items = result.Task?.Data?.filter((i) => i.Key !== null) ?? [];
    return mapper.from(items);
  }

  private getActivitiesQuery(
    param: ActivityQueryParam,
  ): Octopus.LogicalQuery<Octopus.Task> {
    const dateField = param.dateField ?? 'DateTime';
    const query: Octopus.LogicalQuery<Octopus.Task> = {
      $AND: [
        {
          [dateField]: param.duedate,
        },
      ],
    };
    if (param.key && param.entryType) {
      this.setQueryByEntryType(query, param);
    } else if (param.key && !param.entryType) {
      this.setQueryByEntityType(query, param);
    }
    this.setBasicQuery(query, param);

    return query;
  }

  private setBasicQuery(
    query: Octopus.LogicalQuery<Octopus.Task>,
    param: ActivityQueryParam,
  ): void {
    if (param.users?.length) {
      query.$AND?.push({
        'AssignedTo/Key': {
          $IN: param.users,
        },
      });
    }

    if (param.creators?.length) {
      if (!param.users?.length) {
        query.$AND?.push({
          'AssignedTo/Key': {
            $NIN: param.creators,
          },
        });
      }
      query.$AND?.push({
        Creator: {
          $IN: param.creators,
        },
      });
    }

    if (!param.showCompleted) {
      query.$AND?.push({
        Completed: {
          $EQ: false,
        },
      });
    }
  }

  private setQueryByEntryType(
    query: Octopus.LogicalQuery<Octopus.Task>,
    param: ActivityQueryParam,
  ): void {
    if (param.entryType === 'addressBook') {
      query.$AND?.push({
        AbEntryKey: {
          $EQ: param.key,
        },
      });
    } else if (param.entryType === 'lead') {
      query.$AND?.push({
        LeadKey: {
          $EQ: param.key,
        },
      });
    } else if (
      param.entryType === 'opportunity' ||
      param.entryType === 'case'
    ) {
      query.$AND?.push({
        WithKey: {
          $EQ: param.key,
        },
      });
    }
  }

  private setQueryByEntityType(
    query: Octopus.LogicalQuery<Octopus.Task>,
    param: ActivityQueryParam,
  ): void {
    if (param.recurrence) {
      const Recurrence: Octopus.LogicalQuery<Octopus.Task> = {
        $AND: [
          {
            RecurrenceKey: {
              $EQ: param.recurrence,
            },
          },
          {
            Completed: {
              $EQ: false,
            },
          },
        ],
      };
      const DualSearch: Octopus.LogicalQuery<Octopus.Task> = {
        $OR: [],
      };
      DualSearch.$OR?.push(Recurrence);
      if (Array.isArray(param.key)) {
        DualSearch.$OR?.push({
          Key: {
            $IN: param.key,
          },
        });
      } else {
        DualSearch.$OR?.push({
          Key: {
            $EQ: param.key,
          },
        });
      }
      query.$AND?.push(DualSearch);
    } else if (Array.isArray(param.key)) {
      query.$AND?.push({
        Key: {
          $IN: param.key,
        },
      });
    } else {
      query.$AND?.push({
        Key: {
          $EQ: param.key,
        },
      });
    }
  }

  private getRequest(
    query: Octopus.LogicalQuery<Octopus.Task> | Octopus.Query<Octopus.Task>,
    showDisabledUsers?: boolean,
    columns?: ColumnWithTranslation[],
  ): Octopus.TaskReadRequest {
    const fields: Octopus.Scope<Octopus.Task> = {
      Key: 1,
      Completed: 1,
      Activity: 1,
      Priority: 1,
      Category: {
        Value: 1,
        DisplayValue: 1,
      },
      AbEntry: { Key: 1, DisplayValue: 1 },
      Lead: { Key: 1, DisplayValue: 1 },
      WithKey: 1,
      With: {
        Opportunity: {
          Key: 1,
          Objective: 1,
        },
        Case: {
          Key: 1,
          CaseNumber: 1,
          Subject: 1,
        },
      },
      RecurrenceKey: 1,
      RecurrenceType: 1,
      ActionPlanKey: 1,
      SecStatus: {
        CanRead: 1,
        CanCreate: 1,
        CanUpdate: 1,
        CanDelete: 1,
      },
      EffectiveDate: { Value: 1, DisplayValue: 1 },
      DateTime: { Value: 1, DisplayValue: 1 },
      CompletionDate: { Value: 1, DisplayValue: 1 },
      CompletedBy: { Key: 1, DisplayValue: 1 },
      Creator: { Key: 1, DisplayValue: 1 },
      AssignedTo: { Key: 1, DisplayValue: 1 },
      WorkflowInstanceKey: 1,
    };

    const columnsfield: Octopus.Columns = {};

    columns?.forEach((column: ColumnWithTranslation, index) => {
      columnsfield[TaskService.columnKey(index)] = {
        Value: column.field,
        SortValue: 1,
      };
    });

    return {
      Task: {
        Scope: {
          Fields: fields,
          Columns: columnsfield,
        },
        Criteria: {
          SearchQuery: query,
        },
        OrderBy: {
          Fields: {},
        },
        Options: {
          IncludeDisabled: showDisabledUsers,
        },
      },
      Compatibility: { TaskObject: '2.0' },
      Globalization: Octopus.DefaultGlobalization,
    };
  }

  private post<TResponse>(
    action: string,
    request: unknown,
    useHeaders = true,
  ): Observable<TResponse> {
    return this.http.post<TResponse>(
      `${this.context.api}${action}`,
      request,
      useHeaders ? { headers: this.getRequestHeaders() } : undefined,
    );
  }

  private isSuccessful(result?: { Code?: number } | null): boolean {
    return result?.Code === Octopus.ResponseStatusCode.Successful;
  }

  private getRequestHeaders(): { [header: string]: string } {
    return {
      [insightsHeader('entity')]: 'Task',
    };
  }
}
